import { LoadingOutlined, YoutubeOutlined } from "@ant-design/icons"
import { Alert, Button, Form, Input, Space, Typography } from "antd"
import type React from "react"
import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { DragModal } from "@/components/Modal"
import { VideoApiError, videoService } from "@/services/video/videoApi"

const { Text } = Typography

interface VideoCreateModalProps {
  open: boolean
  onClose: () => void
  onSuccess: () => void
}

export const VideoCreateModal: React.FC<VideoCreateModalProps> = ({ open, onClose, onSuccess }) => {
  const navigate = useNavigate()
  const [form] = Form.useForm()
  const [creating, setCreating] = useState(false)
  const [seconds, setSeconds] = useState(0)
  const [error, setError] = useState("")
  const [existingId, setExistingId] = useState<string>()

  useEffect(() => {
    if (!creating) return
    const started = Date.now()
    const timer = setInterval(() => setSeconds(Math.floor((Date.now() - started) / 1000)), 1000)
    return () => clearInterval(timer)
  }, [creating])

  useEffect(() => {
    if (open) {
      form.resetFields()
      setError("")
      setExistingId(undefined)
      setSeconds(0)
    }
  }, [open, form])

  const handleCreate = async () => {
    try {
      const values = await form.validateFields()
      const url = values.url.trim()

      if (
        !/^[\w-]{11}$/.test(url) &&
        !/^https?:\/\/(?:www\.|m\.)?(?:youtube\.com|youtu\.be|youtube-nocookie\.com)\//i.test(url)
      ) {
        setError("Vui lòng nhập URL YouTube hoặc Video ID (11 ký tự) hợp lệ.")
        return
      }

      setCreating(true)
      setError("")
      setExistingId(undefined)
      setSeconds(0)

      const video = await videoService.create(url)
      onSuccess()
      onClose()
      navigate(`/videos/${video.id}`)
    } catch (cause) {
      if (cause instanceof VideoApiError) {
        setError(cause.message)
        setExistingId(cause.existingId)
      } else if (cause instanceof Error) {
        setError(cause.message)
      } else {
        setError("Không thể thêm video. Vui lòng thử lại.")
      }
    } finally {
      setCreating(false)
    }
  }

  return (
    <DragModal
      centered
      title={
        <div className="flex items-center gap-2">
          <YoutubeOutlined className="text-red-500 text-xl" />
          <span>Thêm video YouTube mới</span>
        </div>
      }
      open={open}
      onCancel={creating ? undefined : onClose}
      width={560}
      destroyOnHidden
      closable={!creating}
      footer={
        <Space>
          <Button onClick={onClose} disabled={creating}>
            Hủy
          </Button>
          <Button type="primary" onClick={handleCreate} loading={creating}>
            {creating ? `Đang xử lý (${seconds}s)...` : "Lấy dữ liệu & Tạo video"}
          </Button>
        </Space>
      }
    >
      <div className="py-2">
        <Form form={form} layout="vertical" disabled={creating} onFinish={handleCreate}>
          <Form.Item
            name="url"
            label={<span className="font-medium">URL YouTube hoặc Video ID</span>}
            rules={[
              { required: true, message: "Vui lòng nhập link YouTube hoặc Video ID!" },
              { whitespace: true, message: "URL không được để trống!" },
            ]}
            extra={
              <Text type="secondary" className="text-xs">
                Ví dụ: <code>https://www.youtube.com/watch?v=dQw4w9WgXcQ</code> hoặc <code>dQw4w9WgXcQ</code>
              </Text>
            }
          >
            <Input
              allowClear
              placeholder="Dán đường link YouTube vào đây..."
              prefix={<YoutubeOutlined className="text-red-500" />}
              onPressEnter={handleCreate}
              autoFocus
            />
          </Form.Item>
        </Form>

        {creating && (
          <div className="mt-4 rounded-lg border border-blue-100 bg-blue-50/70 p-3.5">
            <div className="flex items-center gap-3">
              <LoadingOutlined className="text-blue-600 text-lg" />
              <div className="flex-1">
                <div className="font-medium text-blue-900 text-sm">
                  Đang phân tích video & trích xuất phụ đề ({seconds}s)...
                </div>
                <div className="mt-0.5 text-blue-700 text-xs">
                  Hệ thống đang tải phụ đề gốc JSON3 và cấu trúc hóa dữ liệu câu/từ.
                </div>
              </div>
            </div>
          </div>
        )}

        {error && (
          <Alert
            className="mt-4"
            type="error"
            showIcon
            message="Không thể tạo video"
            description={error}
            action={
              existingId ? (
                <Button
                  size="small"
                  type="primary"
                  onClick={() => {
                    onClose()
                    navigate(`/videos/${existingId}`)
                  }}
                >
                  Mở video đã có
                </Button>
              ) : undefined
            }
          />
        )}
      </div>
    </DragModal>
  )
}
